import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Instagram-style horizontal photo carousel with:
 *  - swipe on touch, drag on mouse
 *  - prev/next chevrons on hover (desktop)
 *  - counter (2/5) and dots
 *  - click any image to open fullscreen
 */
export default function PostCarousel({ urls = [], onOpenFullscreen, testidPrefix = "post-carousel" }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const startXRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(-${idx * 100}%)`;
    }
  }, [idx]);

  if (!urls || urls.length === 0) return null;
  if (urls.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onOpenFullscreen?.({ url: urls[0], isImage: true })}
        className="block w-full mb-3 rounded-xl overflow-hidden border border-border cursor-zoom-in"
        data-testid={`${testidPrefix}-single`}
      >
        <img src={urls[0]} alt="" className="w-full" />
      </button>
    );
  }

  const go = (delta) => {
    setIdx((i) => Math.min(urls.length - 1, Math.max(0, i + delta)));
  };

  const onPointerDown = (e) => {
    startXRef.current = e.clientX;
    draggingRef.current = true;
  };
  const onPointerUp = (e) => {
    if (!draggingRef.current || startXRef.current == null) return;
    const dx = e.clientX - startXRef.current;
    if (dx <= -40) go(1);
    else if (dx >= 40) go(-1);
    draggingRef.current = false;
    startXRef.current = null;
  };

  return (
    <div
      className="relative mb-3 rounded-xl overflow-hidden border border-border bg-black select-none group"
      data-testid={testidPrefix}
    >
      <div
        ref={trackRef}
        className="flex transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { draggingRef.current = false; startXRef.current = null; }}
      >
        {urls.map((u, i) => (
          <div key={u + "-" + i} className="min-w-full flex items-center justify-center">
            <img
              src={u}
              alt=""
              draggable={false}
              onClick={() => onOpenFullscreen?.({ url: u, isImage: true, urls, index: i })}
              className="w-full max-h-[70vh] object-contain cursor-zoom-in"
              data-testid={`${testidPrefix}-item-${i}`}
            />
          </div>
        ))}
      </div>

      {/* counter */}
      <div
        className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm"
        data-testid={`${testidPrefix}-counter`}
      >
        {idx + 1}/{urls.length}
      </div>

      {/* prev / next arrows (desktop only, appear on hover) */}
      {idx > 0 && (
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Anterior"
          data-testid={`${testidPrefix}-prev`}
          className="absolute top-1/2 -translate-y-1/2 left-2 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {idx < urls.length - 1 && (
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Próximo"
          data-testid={`${testidPrefix}-next`}
          className="absolute top-1/2 -translate-y-1/2 right-2 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* dots */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5" data-testid={`${testidPrefix}-dots`}>
        {urls.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={`Foto ${i + 1}`}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/50"}`}
          />
        ))}
      </div>
    </div>
  );
}
